import { Injectable, inject, signal, effect } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { IndexedDbService, type PendingRemito } from './indexed-db';
import {
  DeliveryNotesApiService,
  type DeliveryNotesCheckoutPayload,
  type DeliveryNotesCheckoutItemPayload
} from './delivery-notes-api';
import { NetworkService } from './network';
import { GeolocationService } from './geolocation';
import { DeviceInfoService } from './device-info.service';
import { FingerprintService } from './fingerprint.service';
import { CheckInLocationSyncService } from './checkin-location-sync';
import { RemitoStateService } from './remito-state';
import { SyncQueueService } from './sync-queue';
import { LogService } from './log.service';
import { dataUrlToFile } from '@shared/utils/file.utils';
import type { CheckOutPayload } from '@core/remito.model';
import { BusyStateService } from '@shared/services/update/busy-state';
import { createRequestId } from '@core/http/http-context';

export interface CheckoutSyncResult {
  success: boolean;
  totalProcessed: number;
  syncedCount: number;
  pendingCount: number;
  failedCount: number;
  errors: { qrCode: string; error: string; status?: number }[];
}

interface SyncDecision {
  action: 'synced' | 'pending' | 'failed';
  error?: string;
  status?: number;
}

@Injectable({ providedIn: 'root' })
export class CheckoutSyncService {
  private readonly db = inject(IndexedDbService);
  private readonly api = inject(DeliveryNotesApiService);
  private readonly network = inject(NetworkService);
  private readonly geolocation = inject(GeolocationService);
  private readonly deviceInfo = inject(DeviceInfoService);
  private readonly fingerprint = inject(FingerprintService);
  private readonly checkInSync = inject(CheckInLocationSyncService);
  private readonly remitoState = inject(RemitoStateService);
  private readonly syncQueue = inject(SyncQueueService);
  private readonly logger = inject(LogService);
  private readonly busyState = inject(BusyStateService);

  readonly isProcessing = signal(false);
  readonly progress = signal(0);
  readonly total = signal(0);
  readonly processed = signal(0);
  readonly currentRemito = signal<string | null>(null);
  readonly lastResult = signal<CheckoutSyncResult | null>(null);
  readonly lastCheckoutResult = signal<CheckoutSyncResult | null>(null);

  constructor() {
    effect(() => {
      this.busyState.setSendingActive('checkout-sync', this.isProcessing());
    });
  }

  async checkoutActiveSession(): Promise<CheckoutSyncResult> {
    if (this.isProcessing()) {
      throw new Error('Ya hay una sincronización en progreso');
    }

    const requestId = createRequestId('checkout-session');
    this.isProcessing.set(true);
    this.progress.set(0);
    this.processed.set(0);
    this.currentRemito.set(null);

    const result: CheckoutSyncResult = {
      success: true,
      totalProcessed: 0,
      syncedCount: 0,
      pendingCount: 0,
      failedCount: 0,
      errors: []
    };

    try {
      await this.db.init();

      const storedCheckIn = await this.checkInSync.getStoredCheckIn();
      if (!storedCheckIn) {
        throw new Error('No hay un check-in activo');
      }

      const sessionId = storedCheckIn.sessionId;
      const remitos = await this.db.getBySessionId(sessionId, true);

      result.totalProcessed = remitos.length;
      this.total.set(remitos.length);

      if (remitos.length === 0) {
        await this.closeSession();
        this.lastResult.set(result);
        return result;
      }

      const checkoutPayload = await this.buildCheckOutPayload(storedCheckIn.id);

      for (let index = 0; index < remitos.length; index += 1) {
        const remito = remitos[index];
        this.currentRemito.set(remito.qrCode);
        this.processed.set(index + 1);
        this.progress.set(Math.round(((index + 1) / remitos.length) * 100));

        const decision = await this.syncSingle(remito, checkoutPayload, requestId);

        if (decision.action === 'synced') {
          result.syncedCount += 1;
        } else if (decision.action === 'pending') {
          result.pendingCount += 1;
          result.success = false;
          result.errors.push({
            qrCode: remito.qrCode,
            error: decision.error ?? 'Pendiente de sincronización',
            status: decision.status
          });
        } else {
          result.failedCount += 1;
          result.success = false;
          result.errors.push({
            qrCode: remito.qrCode,
            error: decision.error ?? 'Error al sincronizar',
            status: decision.status
          });
        }
      }

      await this.syncQueue.refreshStatusSnapshot();
      await this.closeSession();
      this.lastResult.set(result);
      this.lastCheckoutResult.set(result);
      return result;
    } finally {
      this.isProcessing.set(false);
      this.progress.set(0);
      this.currentRemito.set(null);
    }
  }

  async syncPendingJob(): Promise<CheckoutSyncResult | null> {
    if (this.isProcessing()) {
      return null;
    }

    if (!this.network.isOnline()) {
      return null;
    }

    const requestId = createRequestId('checkout-pending-job');
    this.isProcessing.set(true);
    this.progress.set(0);
    this.processed.set(0);
    this.currentRemito.set(null);

    const result: CheckoutSyncResult = {
      success: true,
      totalProcessed: 0,
      syncedCount: 0,
      pendingCount: 0,
      failedCount: 0,
      errors: []
    };

    try {
      await this.db.init();
      const pending = await this.db.getPending(false);
      const eligible = pending.filter(item => !!item.checkout);
      result.totalProcessed = eligible.length;
      this.total.set(eligible.length);

      if (!eligible.length) {
        this.lastResult.set(result);
        return result;
      }

      for (let index = 0; index < eligible.length; index += 1) {
        const remito = eligible[index];
        this.currentRemito.set(remito.qrCode);
        this.processed.set(index + 1);
        this.progress.set(Math.round(((index + 1) / eligible.length) * 100));

        const checkoutPayload = this.resolveCheckoutForRemito(remito);
        if (!checkoutPayload) {
          result.pendingCount += 1;
          result.success = false;
          result.errors.push({
            qrCode: remito.qrCode,
            error: 'Pendiente de check-out para sincronizar'
          });
          continue;
        }
        const decision = await this.syncSingle(remito, checkoutPayload, requestId);

        if (decision.action === 'synced') {
          result.syncedCount += 1;
        } else if (decision.action === 'pending') {
          result.pendingCount += 1;
          result.success = false;
          result.errors.push({
            qrCode: remito.qrCode,
            error: decision.error ?? 'Pendiente de sincronización',
            status: decision.status
          });
        } else {
          result.failedCount += 1;
          result.success = false;
          result.errors.push({
            qrCode: remito.qrCode,
            error: decision.error ?? 'Error al sincronizar',
            status: decision.status
          });
        }
      }

      await this.syncQueue.refreshStatusSnapshot();
      this.lastResult.set(result);
      return result;
    } finally {
      this.isProcessing.set(false);
      this.progress.set(0);
      this.currentRemito.set(null);
    }
  }

  async closeEmptyActiveSession(): Promise<void> {
    await this.db.init();

    const storedCheckIn = await this.checkInSync.getStoredCheckIn();
    if (!storedCheckIn) {
      throw new Error('No hay un check-in activo');
    }

    const remitos = await this.db.getBySessionId(storedCheckIn.sessionId, true);
    if (remitos.length > 0) {
      throw new Error('No se puede cerrar la sesión porque hay remitos pendientes de check-out');
    }

    await this.closeSession();
  }

  private async syncSingle(remito: PendingRemito, checkOut: CheckOutPayload, requestId: string): Promise<SyncDecision> {
    await this.db.updateStatus(remito.id, 'syncing');
    await this.db.incrementAttempts(remito.id);

    const updatedRemito: PendingRemito = {
      ...remito,
      checkout: checkOut
    };
    await this.db.update(updatedRemito);

    const payload: DeliveryNotesCheckoutPayload = {
      sessionId: remito.sessionId,
      checkIn: remito.checkIn,
      checkout: checkOut,
      qrCode: remito.qrCode,
      scanDate: new Date(remito.createdAt).toISOString(),
      location: checkOut.location,
      observation: remito.observation ?? '',
      imageKeys: remito.imageKeys,
      deliveryNotes: remito.imageKeys.map((imageKey, index) => this.toDeliveryNotePayload(remito, checkOut, imageKey, index))
    };

    try {
      this.assertAlignedImages(remito);
      const images = await Promise.all(
        remito.images.map(async (image, index) => ({
          file: await dataUrlToFile(image, remito.imageKeys[index]),
          imageName: remito.imageKeys[index]
        }))
      );
      this.logger.info('Checkout listo para sincronizar', {
        requestId,
        qrCodeSuffix: payload.qrCode.slice(-6),
        imageCount: images.length,
        imageKeys: images.map(item => item.imageName)
      });
      await this.api.checkout({
        payload,
        images,
        imageKeys: remito.imageKeys,
        requestId
      });
      await this.db.delete(remito.id);
      await this.syncQueue.refreshStatusSnapshot();
      return { action: 'synced' };
    } catch (error) {
      const decision = this.classifyError(error);
      if (decision.action === 'synced') {
        await this.db.delete(remito.id);
        await this.syncQueue.refreshStatusSnapshot();
        return decision;
      }
      await this.db.updateStatus(remito.id, decision.action === 'failed' ? 'failed' : 'pending', decision.error);
      await this.syncQueue.refreshStatusSnapshot();
      return decision;
    }
  }

  private classifyError(error: unknown): SyncDecision {
    if (error instanceof HttpErrorResponse) {
      const status = error.status;
      if (status === 409) {
        return { action: 'synced', status };
      }
      if (status === 0 || status === 408 || status === 429 || status >= 500) {
        return { action: 'pending', status, error: this.extractErrorMessage(error) };
      }
      if ([400, 401, 403, 404, 422].includes(status)) {
        return { action: 'failed', status, error: this.extractErrorMessage(error) };
      }
      return { action: 'pending', status, error: this.extractErrorMessage(error) };
    }

    if (error instanceof Error && error.name === 'TimeoutError') {
      return { action: 'pending', error: 'La solicitud excedió el tiempo de espera' };
    }

    if (error instanceof Error) {
      return { action: 'pending', error: error.message };
    }

    return { action: 'pending', error: 'Error desconocido' };
  }

  private extractErrorMessage(error: HttpErrorResponse): string {
    return error.error?.message || error.message || 'Error al comunicarse con el servidor';
  }

  private async buildCheckOutPayload(checkInId: string): Promise<CheckOutPayload> {
    let location = this.geolocation.getCachedLocation(5 * 60 * 1000);
    if (!location) {
      try {
        location = await this.geolocation.obtenerUbicacion();
      } catch (error) {
        this.logger.warn('No se pudo obtener ubicación de check-out, usando fallback', {
          error: error instanceof Error ? error.message : String(error)
        });
      }
    }

    if (!location) {
      const stored = await this.checkInSync.getStoredCheckIn();
      if (stored?.ubicacion) {
        location = stored.ubicacion;
      }
    }

    if (!location) {
      throw new Error('No se pudo determinar la ubicación de check-out');
    }

    const deviceInfo = await this.deviceInfo.getDeviceInfo();
    const fingerprintId = await this.fingerprint.getVisitorId();

    return {
      id: `checkout-${checkInId}`,
      createdAt: new Date().toISOString(),
      location: {
        latitude: location.latitud,
        longitude: location.longitud,
        precision: location.precision
      },
      deviceInfo: {
        ...deviceInfo,
        fingerprintId
      }
    };
  }

  private resolveCheckoutForRemito(remito: PendingRemito): CheckOutPayload | null {
    const existing = remito.checkout;
    if (existing?.createdAt && existing.location) {
      return {
        id: existing.id ?? `checkout-${remito.checkIn.id}`,
        createdAt: existing.createdAt,
        location: existing.location,
        deviceInfo: existing.deviceInfo ?? remito.checkIn.deviceInfo
      };
    }
    return null;
  }

  private toDeliveryNotePayload(
    remito: PendingRemito,
    checkOut: CheckOutPayload,
    imageKey: string,
    index: number
  ): DeliveryNotesCheckoutItemPayload {
    return {
      qrCode: remito.qrCode,
      scanDate: new Date(remito.createdAt).toISOString(),
      location: checkOut.location,
      observation: remito.observation ?? '',
      imageKeys: [imageKey],
      pageNumber: index + 1
    };
  }

  private assertAlignedImages(remito: PendingRemito): void {
    if (remito.images.length === 0 || remito.imageKeys.length === 0) {
      throw new Error('No hay imágenes para sincronizar');
    }

    if (remito.images.length !== remito.imageKeys.length) {
      throw new Error('La cantidad de imágenes no coincide con la cantidad de nombres de archivo');
    }
  }

  private async closeSession(): Promise<void> {
    try {
      this.remitoState.reset({ clearCheckIn: true });
      await this.geolocation.limpiarCheckInGuardado();
    } catch (error) {
      this.logger.warn('No se pudo cerrar la sesión de check-in', {
        error: error instanceof Error ? error.message : String(error)
      });
    }
  }
}
